import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import RefreshIcon from "@mui/icons-material/Refresh";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrSectionPanel from "../../../../components/BdrSectionPanel/BdrSectionPanel";
import BdrDisclosure from "../../../../components/BdrDisclosure/BdrDisclosure";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrCheckbox from "../../../../components/BdrCheckbox/BdrCheckbox";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import {
  ASSIGN_TOURS,
  RELEASE_TOUR,
  TOUR_REPORT,
  UNASSIGN_TOUR,
  UNRELEASE_TOUR,
} from "../../../../tours/tourGql";
import WEB_TOUR_STEPS from "../../../../tours/registry";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import {
  PLATFORM_LABEL,
  STATUS_LABEL,
  TABS,
  assignSummary,
  canAssign,
  mutationErrorText,
  peopleFromReport,
  tabCounts,
  toursForTab,
} from "./walkthroughsModel";
// The tile row reuses the Tasks console's tile classes (style guide §6e names
// them the reference for the next tile grid) instead of restating the recipe.
import "../../adminComponents/AttentionTiles/attentionTiles.css";
import "./walkthroughsPage.css";

/**
 * Walkthroughs — the admin page for the feature tours (registry §36.10).
 *
 * One section panel; the tab strip in its header (All · Web · Mobile · People,
 * the global `.bdr-tabs` recipe — this page is its first consumer). The three
 * platform tabs show one COLLAPSED `BdrDisclosure` row per tour: title, platform,
 * release date, how many people it applies to. Expanded: the summary, the five
 * count tiles, where people stopped, and one row per person with Assign /
 * Unassign. The People tab turns that around: one row per person, their tours,
 * what's due.
 *
 * Assigning is the admin control: an assigned walkthrough is a to-do that
 * stays in the person's What's New (no Dismiss) until they complete it
 * (server rule, registry §36.3). Both mutations are the server's existing
 * `assignTours` / `unassignTour`; the page refetches the report after a batch.
 *
 * Step labels come from the web registry, so a web step reads by its title
 * rather than a bare index; mobile steps live in the app, so theirs read
 * "Step N". An OPTIONAL step whose anchor wasn't on screen reads as a normal
 * skip instead of a fault (the engine logs both as anchor_missing).
 */

// Why a tour ended early (the engine's exit reasons).
const REASON_LABEL = {
  skip: "pressed Exit",
  esc: "pressed Esc",
  nav: "left the page",
  anchor_missing: "out of date (highlight not found)",
  action_missing: "out of date (couldn't open what it needed)",
  modal_missing: "out of date (a window didn't open)",
  route_unreachable: "page not reachable",
  modal_stuck: "a window wouldn't close",
  route_in_stack: "went back to the page",
  action_declined: "was in the middle of something",
  error: "error",
  disabled: "tours switched off",
  logout: "logged out",
  restart: "started another tour",
};

const fmtDate = (ms) => (ms ? BAGroupTimeFormat(ms, false, true) : "");

function stepLabel(tourId, stepIndex) {
  if (stepIndex == null) return "Before the first step";
  const step = WEB_TOUR_STEPS[tourId]?.steps?.[stepIndex];
  const n = `Step ${stepIndex + 1}`;
  return step?.title ? `${n} · ${step.title}` : n;
}

/*
 * One row per real outcome. A fault on a REQUIRED step is logged twice by the
 * engine — the anchor_missing / action_missing event, then `exited` with that
 * reason — so only the `exited` row is shown for it. On an OPTIONAL step the
 * fault is the only record (the tour skipped on), and it reads as a skip.
 */
const isOptional = (tourId, stepIndex) =>
  !!WEB_TOUR_STEPS[tourId]?.steps?.[stepIndex]?.optional;

function visibleSteps(tour) {
  return tour.steps.filter(
    (s) => s.event === "exited" || isOptional(tour.id, s.stepIndex)
  );
}

function stepRowText(tourId, s) {
  if (s.event !== "exited") return "skipped (nothing to show on the page)";
  return REASON_LABEL[s.reason] || s.reason || "stopped";
}

const people = (n) => `${n} ${n === 1 ? "person" : "people"}`;

const PlatformChip = ({ platform }) => (
  <span className="bdr-badge">{PLATFORM_LABEL[platform] || platform}</span>
);

const StatusBadge = ({ status }) => (
  <span className={`bdr-badge${status === "completed" ? " bdr-badge--accent" : ""}`}>
    {STATUS_LABEL[status] || status}
  </span>
);

/**
 * Pick several people for one tour. Lists only those who can be assigned (not
 * completed, not already assigned) — the same set the server accepts.
 */
function AssignPicker({ tour, busy, onClose, onAssign }) {
  const candidates = tour.users.filter(canAssign);
  const [picked, setPicked] = useState(() => new Set());
  const toggle = (userId) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  const all = candidates.length > 0 && picked.size === candidates.length;
  return (
    <BdrModal
      open
      onClose={onClose}
      title={`Assign: ${tour.title}`}
      size="sm"
      actions={
        <>
          <BdrButton onClick={onClose} disabled={busy}>
            Cancel
          </BdrButton>
          <BdrButton
            variant="confirm"
            disabled={busy || picked.size === 0}
            onClick={() => onAssign(candidates.filter((u) => picked.has(u.userId)))}
          >
            Assign ({picked.size})
          </BdrButton>
        </>
      }
    >
      {candidates.length === 0 ? (
        <p className="bdr-empty">Everyone eligible is already assigned or has completed it.</p>
      ) : (
        <div className="wt-picker">
          <BdrCheckbox
            label="Everyone"
            size="sm"
            checked={all}
            indeterminate={!all && picked.size > 0}
            onChange={() => setPicked(all ? new Set() : new Set(candidates.map((u) => u.userId)))}
          />
          <ul className="wt-picker-list">
            {candidates.map((u) => (
              <li key={u.userId}>
                <BdrCheckbox
                  size="sm"
                  label={u.name}
                  helper={STATUS_LABEL[u.status] || u.status}
                  checked={picked.has(u.userId)}
                  onChange={() => toggle(u.userId)}
                />
              </li>
            ))}
          </ul>
          <p className="wt-picker-note">
            An assigned walkthrough stays in their What's New, with no Dismiss, until they complete it.
          </p>
        </div>
      )}
    </BdrModal>
  );
}

const WalkthroughsPage = () => {
  const { handleSnack } = useSnackbar();
  const [tab, setTab] = useState("all");
  const [report, setReport] = useState(null);
  const [failed, setFailed] = useState(false);
  // Open rows, by key (`tour:<id>` / `person:<userId>`). Everything starts closed.
  const [open, setOpen] = useState(() => new Set());
  const [picker, setPicker] = useState(null); // the tour whose Assign… picker is open
  const [busy, setBusy] = useState(false);

  const [fetchReport, { loading }] = useLazyQuery(TOUR_REPORT, {
    fetchPolicy: "network-only",
  });
  const [assignMut] = useMutation(ASSIGN_TOURS);
  const [unassignMut] = useMutation(UNASSIGN_TOUR);
  const [releaseMut] = useMutation(RELEASE_TOUR);
  const [unreleaseMut] = useMutation(UNRELEASE_TOUR);

  /* The execute promise RESOLVES on a GraphQL error (style guide §6e), so the
     error is read off the result; the catch is for link / network throws. */
  const load = useCallback(() => {
    fetchReport()
      .then(({ data, error }) => {
        if (error || !data?.tourReport) {
          setFailed(true);
          return;
        }
        setFailed(false);
        setReport(data.tourReport);
      })
      .catch(() => setFailed(true));
  }, [fetchReport]);

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, [load]);

  const toggle = (key) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /* One call per person (the mutation is per user), all of them settled, then
     ONE refetch and ONE snack — never a refetch race per call. */
  const assign = useCallback(
    async (targets, tourId) => {
      if (!targets.length) return;
      setBusy(true);
      const results = await Promise.allSettled(
        targets.map((t) => assignMut({ variables: { userId: t.userId, tourIds: [tourId] } }))
      );
      setBusy(false);
      const rejected = results.find((r) => r.status === "rejected");
      handleSnack(
        rejected && results.every((r) => r.status === "rejected")
          ? mutationErrorText(rejected.reason, "Couldn't assign — try again.")
          : assignSummary(targets, results),
        5000
      );
      load();
    },
    [assignMut, handleSnack, load]
  );

  const unassign = useCallback(
    async (userId, tourId) => {
      setBusy(true);
      try {
        const { data } = await unassignMut({ variables: { userId, tourId } });
        // false = there was no assignment to remove (a stale row), not a failure.
        handleSnack(data?.unassignTour ? "Assignment removed" : "Already unassigned", 3000);
      } catch (err) {
        handleSnack(mutationErrorText(err, "Couldn't remove the assignment — try again."), 5000);
      }
      setBusy(false);
      load();
    },
    [handleSnack, load, unassignMut]
  );

  /* Release now / Back to preview (registry §36.11). The server writes one
     override row; the page refetches so the row flips from "Releases" to
     "Released (from this page)". Everyone eligible is announced on their
     next load — no deploy. */
  const releaseNow = useCallback(
    async (tourId) => {
      setBusy(true);
      try {
        await releaseMut({ variables: { tourId } });
        handleSnack("Released. Everyone eligible is announced on their next page load.", 4000);
      } catch (err) {
        handleSnack(mutationErrorText(err, "Couldn't release — try again."), 5000);
      }
      setBusy(false);
      load();
    },
    [handleSnack, load, releaseMut]
  );

  const backToPreview = useCallback(
    async (tourId) => {
      setBusy(true);
      try {
        const { data } = await unreleaseMut({ variables: { tourId } });
        handleSnack(
          data?.unreleaseTour ? "Back to preview — on its catalog date again." : "It was not released from here.",
          4000
        );
      } catch (err) {
        handleSnack(mutationErrorText(err, "Couldn't change the release — try again."), 5000);
      }
      setBusy(false);
      load();
    },
    [handleSnack, load, unreleaseMut]
  );

  const tours = useMemo(() => report?.tours || [], [report]);
  const counts = useMemo(() => tabCounts(tours), [tours]);
  const peopleRows = useMemo(() => peopleFromReport(tours), [tours]);

  const tile = (label, count, { clearAtZero = false, title } = {}) => (
    <div
      className={`at-tile${clearAtZero && count === 0 ? " is-clear" : ""}`}
      role="img"
      aria-label={title || `${count} ${label}`}
      title={title || undefined}
    >
      <span className="at-count">{count}</span>
      <span className="at-label">{label}</span>
    </div>
  );

  // The per-person control, the same in a tour row and on the People tab.
  const rowAction = (tourId, u) => {
    if (u.completedAt) return <span className="wt-done">Done</span>;
    if (u.assigned) {
      return (
        <BdrButton size="sm" disabled={busy} onClick={() => unassign(u.userId, tourId)}>
          Unassign
        </BdrButton>
      );
    }
    return (
      <BdrButton
        size="sm"
        variant="action"
        disabled={busy}
        onClick={() => assign([{ userId: u.userId, name: u.name }], tourId)}
      >
        Assign
      </BdrButton>
    );
  };

  const renderTour = (tour) => {
    const key = `tour:${tour.id}`;
    const c = tour.counts;
    const steps = visibleSteps(tour);
    return (
      <BdrDisclosure
        key={key}
        id={`wt-${tour.id.replace(":", "-")}`}
        open={open.has(key)}
        onToggle={() => toggle(key)}
        // The panel body does not scroll, so a sticky row header has nothing to
        // pin against (`.bdr-section-panel` clips) — plain headers.
        sticky={false}
        label={tour.title}
        count={c.eligible}
        status={
          <span className="wt-row-meta">
            <PlatformChip platform={tour.platform} />
            {tour.isPreview ? "Releases" : "Released"} {fmtDate(tour.releasedAt)}
            {tour.releaseOverride && !tour.isPreview && " (from this page)"}
            {tour.isPreview && <span className="bdr-badge bdr-badge--warning">Preview</span>}
          </span>
        }
        action={
          <>
            {/* A preview goes live from here (registry §36.11). The confirm
                is the point: this announces the tour to everyone eligible. */}
            {tour.isPreview && (
              <BdrButton
                size="sm"
                variant="confirm"
                disabled={busy}
                confirm
                confirmTitle="Release this walkthrough now?"
                confirmLabel="Release"
                note="Everyone eligible is announced on their next page load. No deploy is needed, and you can put it back to preview from here."
                onClick={() => releaseNow(tour.id)}
              >
                Release now
              </BdrButton>
            )}
            {tour.releaseOverride && !tour.isPreview && (
              <BdrButton
                size="sm"
                disabled={busy}
                confirm
                confirmTitle="Back to preview?"
                confirmLabel="Back to preview"
                note="The tour goes back to its catalog date and disappears from What's New and Help for everyone but developers. People who already saw it keep their history."
                onClick={() => backToPreview(tour.id)}
              >
                Back to preview
              </BdrButton>
            )}
            <BdrButton
              size="sm"
              variant="action"
              disabled={busy || !tour.users.some(canAssign)}
              onClick={() => setPicker(tour)}
            >
              Assign…
            </BdrButton>
          </>
        }
        className="wt-tour"
        bodyClassName="wt-body"
      >
        <p className="wt-meta">
          {tour.summary}
          <br />
          {tour.roles.join(", ")}
          {tour.isPreview && (
            <>
              <br />
              Not released yet: only developer accounts can see it, so these are
              the people it will apply to.
            </>
          )}
        </p>

        <div className="at-row" role="group" aria-label="Progress">
          {tile("Eligible", c.eligible, {
            title: `${people(c.eligible)} this applies to`,
          })}
          {tile("Completed", c.completed)}
          {tile("Started", c.started, {
            title: `${people(c.started)} started but didn't finish`,
          })}
          {tile("Dismissed", c.dismissed)}
          {tile("Not seen", c.notSeen, { clearAtZero: true })}
        </div>

        <h4 className="wt-subhead">Where people stopped</h4>
        {steps.length === 0 ? (
          <p className="wt-none">Nobody has stopped part way.</p>
        ) : (
          <ul className="wt-steps">
            {steps.map((s) => (
              <li key={`${s.event}-${s.stepIndex}-${s.reason}`} className="wt-step">
                <span className="wt-step-name">{stepLabel(tour.id, s.stepIndex)}</span>
                <span className="wt-step-why">
                  {stepRowText(tour.id, s)} · {s.count}× by {people(s.users)}
                </span>
              </li>
            ))}
          </ul>
        )}

        <h4 className="wt-subhead">People</h4>
        {tour.users.length === 0 ? (
          <p className="wt-none">No active users hold these roles.</p>
        ) : (
          <table className="bdr-table wt-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Status</th>
                <th scope="col">When</th>
                <th scope="col">Stopped at</th>
                <th scope="col" aria-label="Assignment" />
              </tr>
            </thead>
            <tbody>
              {tour.users.map((u) => (
                <tr key={u.userId}>
                  <td data-label="Name">
                    {u.name}
                    {u.assigned && !u.completedAt && (
                      <span className="wt-assigned"> · assigned {fmtDate(u.assignedAt)}</span>
                    )}
                  </td>
                  <td data-label="Status">
                    <StatusBadge status={u.status} />
                  </td>
                  <td data-label="When">{fmtDate(u.completedAt || u.seenAt) || "—"}</td>
                  <td data-label="Stopped at">
                    {u.status === "completed" || u.lastExitStep == null
                      ? "—"
                      : `${stepLabel(tour.id, u.lastExitStep)} (${
                          REASON_LABEL[u.lastExitReason] || u.lastExitReason || "stopped"
                        })`}
                  </td>
                  <td className="bdr-table-actions">{rowAction(tour.id, u)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </BdrDisclosure>
    );
  };

  const renderPerson = (p) => {
    const key = `person:${p.userId}`;
    const status = p.due
      ? { tone: "warning", text: `${p.due} due` }
      : p.done
        ? "All done"
        : "Nothing due";
    return (
      <BdrDisclosure
        key={key}
        id={`wt-person-${p.userId}`}
        open={open.has(key)}
        onToggle={() => toggle(key)}
        sticky={false}
        label={p.name}
        count={p.tours.length}
        status={status}
        className="wt-person"
        bodyClassName="wt-body"
      >
        <table className="bdr-table wt-table">
          <thead>
            <tr>
              <th scope="col">Walkthrough</th>
              <th scope="col">Platform</th>
              <th scope="col">Status</th>
              <th scope="col">Assigned</th>
              <th scope="col" aria-label="Assignment" />
            </tr>
          </thead>
          <tbody>
            {p.tours.map((t) => (
              <tr key={t.tourId}>
                <td data-label="Walkthrough">
                  {t.title}
                  {t.isPreview && <span className="bdr-badge bdr-badge--warning">Preview</span>}
                </td>
                <td data-label="Platform">
                  <PlatformChip platform={t.platform} />
                </td>
                <td data-label="Status">
                  <StatusBadge status={t.status} />
                </td>
                <td data-label="Assigned">{t.assigned && !t.completedAt ? fmtDate(t.assignedAt) || "yes" : "—"}</td>
                <td className="bdr-table-actions">
                  {rowAction(t.tourId, { userId: p.userId, name: p.name, ...t })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </BdrDisclosure>
    );
  };

  const tabStrip = (
    <div className="bdr-tabs" role="tablist" aria-label="Walkthrough views">
      {TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          id={`wt-tab-${t.key}`}
          aria-selected={tab === t.key}
          aria-controls="wt-tabpanel"
          className={`bdr-tab${tab === t.key ? " bdr-tab-active" : ""}`}
          onClick={() => setTab(t.key)}
        >
          {t.label}
          {report ? ` (${counts[t.key]})` : ""}
        </button>
      ))}
    </div>
  );

  const shownTours = toursForTab(tours, tab);

  return (
    <div className="bdr-page wt-page">
      <BdrSectionPanel
        title="Walkthroughs"
        className="wt-panel"
        actions={
          <BdrButton
            variant="action"
            size="sm"
            icon={<RefreshIcon />}
            onClick={load}
            disabled={loading || busy}
          >
            Refresh
          </BdrButton>
        }
        headerExtra={tabStrip}
      >
        <div id="wt-tabpanel" role="tabpanel" aria-labelledby={`wt-tab-${tab}`} className="wt-list">
          {report && !report.enabled && (
            <p className="bdr-alert bdr-alert--warning wt-notice" role="status">
              Walkthroughs are switched off on the server (TOURS_ENABLED), so nobody is
              seeing them right now. The history below is still accurate.
            </p>
          )}

          {failed && (
            <p className="bdr-alert bdr-alert--danger wt-notice" role="alert">
              Couldn't load the walkthrough report.
            </p>
          )}

          {!report && !failed && <div className="bdr-loading">Loading…</div>}

          {report && tab !== "people" && shownTours.length === 0 && (
            <div className="bdr-empty">No walkthroughs here yet.</div>
          )}
          {report && tab !== "people" && shownTours.map(renderTour)}

          {report && tab === "people" && peopleRows.length === 0 && (
            <div className="bdr-empty">No active users hold a walkthrough role.</div>
          )}
          {report && tab === "people" && peopleRows.map(renderPerson)}
        </div>
      </BdrSectionPanel>

      {picker && (
        <AssignPicker
          tour={tours.find((t) => t.id === picker.id) || picker}
          busy={busy}
          onClose={() => setPicker(null)}
          onAssign={(targets) => {
            setPicker(null);
            assign(targets, picker.id);
          }}
        />
      )}
    </div>
  );
};

export default WalkthroughsPage;
